import { useState, useRef, useCallback } from "react";
import { supabase } from "@/lib/supabase";

export interface GalleryImage {
  id?: string;
  url: string;
  caption?: string;
  file?: File;
}

interface MultiImageUploaderProps {
  images: GalleryImage[];
  onChange: (images: GalleryImage[]) => void;
  projectName: string;
  maxImages?: number;
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .substring(0, 50);
}

export default function MultiImageUploader({
  images,
  onChange,
  projectName,
  maxImages = 20,
}: MultiImageUploaderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const folderPath = slugify(projectName) || "project-images";

  const handleUpload = useCallback(
    async (files: FileList) => {
      if (images.length + files.length > maxImages) {
        setError(`Maximum ${maxImages} images allowed.`);
        return;
      }

      const validFiles = Array.from(files).filter((f) => {
        if (!f.type.startsWith("image/")) {
          setError(`Skipped "${f.name}" — not an image.`);
          return false;
        }
        if (f.size > 8 * 1024 * 1024) {
          setError(`Skipped "${f.name}" — exceeds 8MB.`);
          return false;
        }
        return true;
      });

      if (validFiles.length === 0) return;

      setUploading(true);
      setError(null);

      const newImages: GalleryImage[] = [];
      const failedFiles: string[] = [];

      for (const file of validFiles) {
        try {
          const fileExt = file.name.split(".").pop() || "jpg";
          const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}.${fileExt}`;
          const filePath = `${folderPath}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from("images")
            .upload(filePath, file, { cacheControl: "3600", upsert: false });

          if (uploadError) {
            // Try uploading without folder first if bucket issue
            if (
              uploadError.message?.includes("does not exist") ||
              uploadError.message?.includes("not found") ||
              uploadError.message?.includes("bucket")
            ) {
              failedFiles.push(file.name);
              continue;
            }
            throw uploadError;
          }

          const { data: urlData } = supabase.storage
            .from("images")
            .getPublicUrl(filePath);

          if (urlData?.publicUrl) {
            newImages.push({ url: urlData.publicUrl, file });
          }
        } catch (err: any) {
          console.error("Upload error:", err);
          failedFiles.push(file.name);
        }
      }

      if (failedFiles.length > 0) {
        setError(
          `Could not upload ${failedFiles.length} image(s). Storage bucket may not be configured. You can paste image URLs manually below.`
        );
      }

      if (newImages.length > 0) {
        onChange([...images, ...newImages]);
      }

      setUploading(false);
    },
    [images, onChange, maxImages, folderPath]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (e.dataTransfer.files.length > 0) {
        handleUpload(e.dataTransfer.files);
      }
    },
    [handleUpload]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleUpload(e.target.files);
      }
      if (inputRef.current) inputRef.current.value = "";
    },
    [handleUpload]
  );

  const removeImage = (idx: number) => {
    const updated = images.filter((_, i) => i !== idx);
    onChange(updated);
  };

  const updateCaption = (idx: number, caption: string) => {
    const updated = images.map((img, i) =>
      i === idx ? { ...img, caption } : img
    );
    onChange(updated);
  };

  const addUrlImage = () => {
    onChange([...images, { url: "", caption: "" }]);
  };

  const updateUrl = (idx: number, url: string) => {
    const updated = images.map((img, i) =>
      i === idx ? { ...img, url } : img
    );
    onChange(updated);
  };

  const moveImage = (idx: number, direction: -1 | 1) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= images.length) return;
    const updated = [...images];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    onChange(updated);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Dropzone */}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-200 ${
          isDragging
            ? "border-primary bg-primary/5"
            : "border-stone-200 hover:border-stone-300 bg-stone-50/50"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />

        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <i className="ri-loader-4-line animate-spin text-primary text-xl"></i>
            <span className="text-sm text-stone-500">Uploading images...</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center text-stone-300">
              <i className="ri-gallery-upload-line text-2xl"></i>
            </div>
            <p className="text-sm text-stone-500 font-medium">
              Drag &amp; drop images here
            </p>
            <p className="text-xs text-stone-400">
              or click to browse — JPG, PNG, WebP (max 8MB each, up to {maxImages})
            </p>
            <p className="text-xs text-primary/70 font-medium">
              Folder: <code className="bg-stone-100 px-1 py-0.5 rounded text-stone-600">{folderPath}/</code>
            </p>
          </div>
        )}
      </div>

      {/* Add URL button */}
      <button
        type="button"
        onClick={addUrlImage}
        className="flex items-center gap-1.5 text-sm text-primary font-medium hover:text-primary-dark transition-colors cursor-pointer self-start"
      >
        <i className="ri-add-circle-line"></i>
        Add image by URL
      </button>

      {/* Image grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {images.map((img, idx) => (
            <div
              key={`${img.url}-${idx}`}
              className="relative group bg-white rounded-xl border border-stone-200 overflow-hidden flex flex-col"
            >
              {/* Image preview */}
              <div className="relative h-28 bg-stone-100">
                {img.url ? (
                  <img
                    src={img.url}
                    alt="Gallery preview"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        "https://readdy.ai/api/search-image?query=placeholder%20image%20icon%20minimal%20grey%20background%20abstract%20design%20simple%20geometric%20shape&width=200&height=150&seq=placeholder&orientation=landscape";
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-stone-300">
                    <i className="ri-image-line text-2xl"></i>
                  </div>
                )}

                {/* Remove button */}
                <button
                  type="button"
                  onClick={() => removeImage(idx)}
                  className="absolute top-1.5 right-1.5 w-6 h-6 flex items-center justify-center bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer shadow-sm"
                  title="Remove"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>

                {/* Move buttons */}
                <div className="absolute bottom-1.5 left-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={() => moveImage(idx, -1)}
                    disabled={idx === 0}
                    className="w-6 h-6 flex items-center justify-center bg-white/90 text-stone-600 rounded-full cursor-pointer shadow-sm disabled:opacity-30"
                  >
                    <i className="ri-arrow-left-line text-xs"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => moveImage(idx, 1)}
                    disabled={idx === images.length - 1}
                    className="w-6 h-6 flex items-center justify-center bg-white/90 text-stone-600 rounded-full cursor-pointer shadow-sm disabled:opacity-30"
                  >
                    <i className="ri-arrow-right-line text-xs"></i>
                  </button>
                </div>
              </div>

              {/* Caption / URL input */}
              <div className="p-2 flex flex-col gap-1.5">
                {!img.url && (
                  <input
                    type="text"
                    value={img.url}
                    onChange={(e) => updateUrl(idx, e.target.value)}
                    placeholder="Paste image URL"
                    className="w-full px-2 py-1 rounded border border-stone-200 text-xs focus:outline-none focus:border-primary text-stone-700 placeholder-stone-400"
                  />
                )}
                <input
                  type="text"
                  value={img.caption || ""}
                  onChange={(e) => updateCaption(idx, e.target.value)}
                  placeholder="Caption (optional)"
                  className="w-full px-2 py-1 rounded border border-stone-200 text-xs focus:outline-none focus:border-primary text-stone-700 placeholder-stone-400"
                />
                <span className="text-[10px] text-stone-400 truncate">
                  {idx + 1} of {images.length}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <i className="ri-error-warning-line"></i>
          {error}
        </p>
      )}
    </div>
  );
}